import clsx from 'clsx';
import type { ReactNode } from 'react';

import { useResponsiveLanes, SINGLE_ITEM_MAX_WIDTH } from '@/hooks/use-responsive-lanes';
import { ContextItemGridContext } from '@/components/context-item/context-item-grid-context';

import { SmartList } from '@/components/smart-list';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Search } from '@flintmint/web-shared/components/search';
import { Button } from '@flintmint/web-shared/components/button';
import { NoData } from '@flintmint/web-shared/components/no-data';

import styles from '@/components/ai-sources-section/ai-context-sources-section.module.scss';

const AUTO_MAX_LANES = 2;
const AUTO_GRID_ITEM_SIZE = 95;
const AUTO_GRID_ROW_GAP = 10;
const AUTO_GRID_DEFAULT_SIZE = AUTO_GRID_ITEM_SIZE + AUTO_GRID_ROW_GAP;
const EMPTY_SELECTIONS: Array<{ id: string }> = [];
const emptyRowProps = () => ({});

export const SourceListLayout = <T extends { id: string }>(props: SourceListLayoutProps<T>) => {
  const {
    data,
    lanes,
    loading,
    resetKey,
    paddingEnd,
    rowRenderer,
    searchValue,
    laneGap = 10,
    defaultSize,
    paddingStart,
    onSearchChange,
    noAttachedView,
    noResultsConfig,
    noAttachedConfig,
    getItemKey,
    onLastItemVisible,
    onAddContextItems,
    showNoAttachedState,
    addActionMode = 'button',
  } = props;

  const hasItems = data.length > 0;
  const state: 'list' | 'no-attached' | 'no-results' =
    loading || hasItems ? 'list' : showNoAttachedState ? 'no-attached' : 'no-results';

  const hasExplicitLanes = lanes != null;
  const { ref, lanes: autoLanes } = useResponsiveLanes(!hasExplicitLanes && state === 'list', {
    singleItemMaxWidth: SINGLE_ITEM_MAX_WIDTH,
    maxLanes: AUTO_MAX_LANES,
  });

  const resolvedLanes = lanes ?? autoLanes;
  const isAutoGrid = !hasExplicitLanes && resolvedLanes > 1;

  return (
    <>
      <div className={styles.contextSectionHeader}>
        {!showNoAttachedState && (
          <div className={styles.searchHolder}>
            <Search size="big" value={searchValue} onChange={(e) => onSearchChange(e.target.value)} />
            {addActionMode === 'icon' ? (
              <Icon type="plus" size={20} onClick={onAddContextItems} data-testid="plus-icon" />
            ) : (
              <Button
                size="big"
                appearance="greyscale"
                color="grey"
                onClick={onAddContextItems}
                icon={<Icon type="plus" size={20} />}
              >
                Add more
              </Button>
            )}
          </div>
        )}
      </div>

      {state === 'no-attached' && (
        <>
          {noAttachedView ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 12,
                flex: 1,
                paddingBottom: 24,
              }}
            >
              {noAttachedView}
            </div>
          ) : noAttachedConfig ? (
            <NoData
              className={noAttachedConfig.className}
              title={noAttachedConfig.title}
              icon={noAttachedConfig.icon}
              description={noAttachedConfig.description}
              actionRenderer={noAttachedConfig.actionRenderer}
            />
          ) : null}
        </>
      )}

      {state === 'no-results' && (
        <NoData
          className={noResultsConfig.className}
          icon={noResultsConfig.icon}
          description={noResultsConfig.getDescription(searchValue)}
        />
      )}

      {state === 'list' && (
        <div ref={ref} className={clsx(styles.listMeasure, { [styles.listMeasureGrid]: isAutoGrid })}>
          <ContextItemGridContext.Provider value={isAutoGrid}>
            <SmartList<T>
              key={resolvedLanes} // remount when lanes change so the virtualizer rebuilds lane measurements
              data={data}
              overscan={8}
              lanes={resolvedLanes}
              selections={EMPTY_SELECTIONS}
              disableRowStates
              loading={loading}
              laneGap={laneGap}
              resetKey={resetKey}
              rowProps={emptyRowProps}
              customDividers={false}
              paddingEnd={paddingEnd}
              rowRenderer={rowRenderer}
              getItemKey={getItemKey}
              paddingStart={paddingStart}
              onLastItemVisible={onLastItemVisible}
              defaultSize={isAutoGrid ? AUTO_GRID_DEFAULT_SIZE : defaultSize}
            />
          </ContextItemGridContext.Provider>
        </div>
      )}
    </>
  );
};

interface SourceListLayoutProps<T extends { id: string }> {
  data: T[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onAddContextItems?: () => void;
  addActionMode?: 'icon' | 'button';
  showNoAttachedState: boolean;
  noAttachedView?: ReactNode;
  noAttachedConfig?: {
    title: string;
    description: string;
    icon: ReactNode;
    className?: string;
    actionRenderer?: () => ReactNode;
  };
  noResultsConfig: {
    icon: ReactNode;
    className?: string;
    getDescription: (searchTerm: string) => string;
  };
  rowRenderer: (item: T) => ReactNode;
  getItemKey?: (item: T) => string;
  resetKey?: string;
  defaultSize: number;
  lanes?: number;
  laneGap?: number;
  paddingStart?: number;
  paddingEnd?: number;
  onLastItemVisible?: (visible: boolean) => void;
}
